/**
 * @file accessRemoval.ts
 * @brief Who has access: who gets the ✕ and what the confirm says (B45).
 *
 * @details
 * DES 01-11-2026: a meeting owner must be able to remove a wrongly invited
 * person. The server sends `canRemove` on each person (never the owner,
 * never the viewer). An older server sends none: shared people follow the
 * top-level `canRemove`, invitees keep no ✕ (it could not remove them).
 *
 * @references
 * - meetingListTools.tsx (SharePanel)
 * - BACKEND boardroom/meetingAccess.service.mjs (listMeetingAccess canRemove)
 * - FRONTEND ShareAccessSheet.tsx (the phone's twin)
 *
 * @author DES
 * @date 01-11-2026
 */

export type RemovableKind = "invitee" | "shared";

/** Show the ✕ on this person? */
export function canRemovePerson(
  person: { canRemove?: boolean | null },
  kind: RemovableKind,
  topLevelCanRemove: boolean,
): boolean {
  if (typeof person.canRemove === "boolean") return person.canRemove;
  return kind === "shared" ? topLevelCanRemove : false;
}

/** The confirm text. An admin is told the owner keeps it and it is logged. */
export function removeConfirmText(
  name: string,
  kind: RemovableKind,
  viewerRole?: "owner" | "admin" | null,
): string {
  const adminNote =
    viewerRole === "admin"
      ? " The owner keeps it, and the removal is written to your company's activity log."
      : "";
  if (kind === "invitee") {
    return `Remove ${name}'s access to this meeting? They won't see it any more.${adminNote}`;
  }
  return viewerRole === "admin"
    ? `Remove this share?\n\n${name} will no longer see this meeting.${adminNote}`
    : `Stop sharing?\n\n${name} will no longer see this meeting.`;
}
